import { Fragment, useEffect, useState } from 'react';
import useDeviceType from '../../Utiles/FindDevice';
import WithdrawAmount from './WithdrawAmount';
import axios from 'axios';
import { refreshTokenV2 } from '../../services/api';
import { baseURL } from '../../services/axiosConfig';
import { CircularLoader } from './MyTransaction';
import moment from 'moment';
import { useDispatch } from 'react-redux';

const Withdraw = ({showWithdraw}) => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [withdrawals, setWithdrawals] = useState([]);
  const deviceType = useDeviceType();
  const [loading, setLoading] = useState(false);
  
  // Add items per page constant
  const ITEMS_PER_PAGE = 10;

  // Helper function to calculate serial number
  const getSerialNumber = (index) => {
    return (currentPage - 1) * ITEMS_PER_PAGE + index + 1;
  };

  const fetchWithdrawals = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseURL}/accounts/withdrawal/user/requests/?page=${page}`, {
        headers: {
          Authorization: `Bearer ${JSON.parse(localStorage.getItem("access_token"))}`,
        },
      });
    
      if (response.status === 200) {
        setWithdrawals(response?.data?.results?.withdrawal_requests || []);
        setTotalPages(Math.ceil(response?.data?.count / ITEMS_PER_PAGE) || 1);
      }
      
      if (response.status === 401) {
        await refreshTokenV2(dispatch);
        return;
      }

    } catch (error) {
      console.error("Error fetching transactions:", error);
      if (error?.response?.status === 401) {
        await refreshTokenV2(dispatch);
        fetchWithdrawals(page);
      } else {
        console.error("Error fetching transactions:", error);
        setWithdrawals([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWithdrawals(currentPage);
  }, [currentPage, showWithdraw]);

  function StatusBadge({ status, isMobile = false }) {
    const isSuccess = status === "Success" || status === "COMPLETED";
    
    return (
      <div className={`inline-flex items-center justify-center px-2 py-1 w-[80px] rounded-full ${isMobile ? 'text-xs' : 'text-xs'} font-medium ${
        isSuccess 
          ? 'bg-green-50 text-green-600 border border-green-200' 
          : 'bg-red-50 text-red-600 border border-red-200'
      }`}>
        <div className={`${isMobile ? 'w-1 h-1' : 'w-1.5 h-1.5'} rounded-full mr-1 ${
          isSuccess ? 'bg-green-500' : 'bg-red-500'
        }`} />
        {isSuccess ? 'Success' : 'Failed'}
      </div>
    );
  }

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  function Pagination({ currentPage, totalPages, onPageChange, isMobile = false }) {
    return (
      <div className={`flex items-center gap-1 ${isMobile ? 'justify-end mt-2' : 'justify-end mt-2'}`}>
        <button 
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`w-8 h-8 flex items-center justify-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 rounded border border-gray-200 bg-white`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7.5 9L4.5 6L7.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`w-8 h-8 flex items-center justify-center ${isMobile ? 'text-xs' : 'text-sm'} font-medium rounded border transition-colors ${
              page === currentPage 
                ? 'bg-blue-500 text-white border-blue-500 shadow-sm' 
                : 'text-gray-600 hover:bg-gray-50 border-gray-200 bg-white'
            }`}
          >
            {page}
          </button>
        ))}
        
        <button 
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`w-8 h-8 flex items-center justify-center ${isMobile ? 'text-xs' : 'text-sm'} text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100 rounded border border-gray-200 bg-white`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.5 3L7.5 6L4.5 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
    );
  }

  if (deviceType === 'mobile') {
    return (
      <div className="px-3 min-h-screen bg-gray-50 mt-4">
        {showWithdraw ? <WithdrawAmount /> : <div className="">
          <div className="overflow-x-auto bg-white">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-[#007fff] to-[#00c05b] text-white">
                  <th className="px-3 py-3 text-left text-xs font-semibold rounded-tl-lg whitespace-nowrap font-archivo">S.No</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold whitespace-nowrap font-archivo">Payment ID</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold whitespace-nowrap font-archivo">Payment Date</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold whitespace-nowrap font-archivo">Payment Amount</th>
                  <th className="px-3 py-3 text-center text-xs font-semibold whitespace-nowrap font-archivo rounded-tr-lg">Request Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? <CircularLoader /> : withdrawals?.length > 0 ? withdrawals?.map((item, index) => (
                  <tr key={index} className={index % 2 !== 0? 'bg-[#f6faff]' : 'bg-white'}>
                    <td className="p-4 border-b border-slate-100 text-gray-700 text-xs last:border-b-0 text-left text-[#00000099] font-archivo">
                      {getSerialNumber(index)}
                    </td>
                    <td className="p-4 border-b border-slate-100 text-gray-700 text-xs last:border-b-0 text-center text-[#00000099] font-archivo">
                      {item.id}
                    </td>
                    <td className="p-4 border-b border-slate-100 text-gray-700 text-xs last:border-b-0 text-center text-[#00000099] font-archivo">
                      {moment(item.request_date).format('D MMM hh:mm a')}
                    </td>
                    <td className="p-4 border-b border-slate-100 text-gray-700 text-xs last:border-b-0 mx-auto flex justify-center items-center">
                      <div className="flex items-center gap-2">
                        <span className={`text-base font-bold ${item.status === 'Failed' ? 'text-red-500' : 'text-green-500'}`}>
                          ↑
                        </span>
                        <span className={`flex items-center gap-0.5 font-semibold font-archivo ${item.status === 'Failed' ? 'text-red-500' : 'text-green-500'}`}>
                          <span className='text-[17px]'>$</span>{item.amount}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 border-b border-slate-100 text-gray-700 text-sm last:border-b-0 text-center">
                      {StatusBadge({status: item.status, isMobile: false})}
                  </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="p-4 text-center text-gray-500">
                      No withdrawal requests found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {withdrawals?.length > 0 && !loading && (
            <div className="p-3 border-t border-gray-200 bg-white">
              <Pagination 
                currentPage={currentPage} 
                totalPages={totalPages} 
                onPageChange={handlePageChange}
                isMobile={true}
              />
            </div>
          )}
        </div>}
      </div>
    );
  }

  return (
    <div className=" mx-auto p-5 font-sans bg-gray-50 rounded-xl">
      {showWithdraw ? <WithdrawAmount />  : <Fragment><div className="bg-white rounded-xl overflow-hidden shadow-sm mb-6 -mx-4 md:mx-0 border-t border-b border-gray-200 md:border md:border-gray-200">
        <div className="overflow-x-auto px-4 md:px-0 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
          <table className="w-full">
            <thead className=''>
              <tr className="bg-gradient-to-r from-[#007fff] to-[#00c05b] text-white">
                <th className="px-3 py-3 text-left text-md font-semibold rounded-tl-lg whitespace-nowrap font-archivo">S.No</th>
                <th className="px-3 py-3 text-center text-md font-semibold whitespace-nowrap font-archivo">Payment ID</th>
                <th className="px-3 py-3 text-center text-md font-semibold whitespace-nowrap font-archivo">Payment Date</th>
                <th className="px-3 py-3 text-center text-md font-semibold whitespace-nowrap font-archivo">Payment Amount</th>
                <th className="px-3 py-3 text-center text-md font-semibold whitespace-nowrap font-archivo">Request Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <CircularLoader /> : withdrawals?.length > 0 ? withdrawals?.map((item, index) => (
                <tr key={index} className={index % 2 !== 0 ? 'bg-[#f6faff]' : 'bg-white'}>
                  <td className="p-4 border-b border-slate-100 text-gray-700 text-sm last:border-b-0 text-left text-[#00000099] font-archivo">
                    {getSerialNumber(index)}
                  </td>
                  <td className="p-4 border-b border-slate-100 text-gray-700 text-sm last:border-b-0 text-center text-[#00000099] font-archivo">
                    {item.id}
                  </td>
                  <td className="p-4 border-b border-slate-100 text-gray-700 text-sm last:border-b-0 text-center text-[#00000099] font-archivo">
                    {moment(item.request_date).format('D MMM hh:mm a')}
                  </td>
                  <td className="p-4 border-b border-slate-100 text-gray-700 text-sm last:border-b-0 mx-auto flex justify-center items-center">
                    <div className="flex items-center gap-2">
                      <span className={`text-base font-bold ${item.status === 'Failed' ? 'text-red-500' : 'text-green-500'}`}>
                        {item.status === 'Failed' ? '↓' : '↑'}
                      </span>
                      <span className={`flex items-center gap-0.5 font-semibold font-archivo ${item.status === 'Failed' ? 'text-red-500' : 'text-green-500'}`}>
                        <span className='text-[17px]'>$</span> {item.amount}
                      </span>
                    </div>
                  </td>
                  <td className="p-4 border-b border-slate-100 text-gray-700 text-sm last:border-b-0 text-center">
                      {StatusBadge({status: item.status, isMobile: false})}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="p-4 text-center text-gray-500">
                    No withdrawal requests found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-end gap-2 items-center">
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          onPageChange={handlePageChange}
        />
      </div>
      </Fragment>}
    </div>
  );
};

export default Withdraw;
